<template>
  <div v-if="resp" class="wrc">
    <section class="report-banner">
      <h3 v-if="ready">Отчет за {{ range }}</h3>
      <h3 v-else>Каждый понедельник<br>отчет за неделю</h3>
      <ZoyaBubble v-if="ready" class="report-observation"><p>{{ observation }}</p></ZoyaBubble>
      <DaysChart v-else-if="example" class="report-example" dark hide-legend :days="example.days" field="sat" total-field="fat" color="#D7AA49" total-color="#EBC06D" :band="example.norms.fat" :limit="example.norms.sat_limit" solid-limit aria-label="Насыщенные жиры — график из примера отчета" />
      <div v-else class="example-placeholder" aria-hidden="true" />
      <p class="banner-lead">Ключевые наблюдения и фокус на<br>следующую неделю</p>
      <p class="progress-count">{{ progress.complete_days }} из 7 дней</p>
      <div class="progress-days" :aria-label="`Полных дней: ${progress.complete_days} из 7`">
        <span v-for="day in progressDays" :key="day.date" :class="{ complete: day.complete }" :aria-label="day.complete ? 'Полный день' : 'Нет полного дня'" />
      </div>
      <p class="banner-note">Для аналитики нужно минимум 4 полных дня записей. Любые — не обязательно подряд. Неполные дни не включаются в расчеты.</p>
      <button class="btn-primary banner-open" @click="open(!ready)">{{ ready ? 'Открыть отчет' : 'Открыть пример' }}</button>
    </section>
    <nav v-if="history.length" class="report-history" aria-label="Ранее созданные отчеты">
      <h3>Прошлые отчеты</h3>
      <RouterLink v-for="report in history" :key="report.start" :to="{path:'/report/week',query:{start:report.start}}">Отчет за {{ weekRange(report.start,report.end) }} <span aria-hidden="true">›</span></RouterLink>
    </nav>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onActivated } from 'vue'
import { useRouter } from 'vue-router'
import api from '../../services/api.js'
import { useAuthStore } from '../../store/auth.js'
import { weekRange, userTz, cardCache as cache } from '../../utils/weekReport.js'
import DaysChart from './DaysChart.vue'
import ZoyaBubble from '../ZoyaBubble.vue'

const router = useRouter()
const authStore = useAuthStore()

const TTL = 2 * 60 * 1000
let inflight = null
let historyInflight = null

const resp = ref(null)
const example = ref(null)
const ready = computed(() => resp.value?.status === 'ready' && !!resp.value?.narrative)
const range = computed(() => weekRange(resp.value?.week?.start, resp.value?.week?.end))
const observation = computed(() => {
  const n = resp.value?.narrative
  return (n?.highlights || n?.wins || []).find(x => typeof x === 'string' && x.trim()) || n?.summary || n?.title || ''
})
const progress = computed(() => {
  if (ready.value) return { complete_days: resp.value.data.complete_days, days: resp.value.data.days }
  return resp.value?.progress || { complete_days: 0, days: [] }
})
const progressDays = computed(() => Array.from({ length: 7 }, (_, i) => progress.value.days?.[i] || { date: String(i), complete: false }))
const history = ref([])

async function load(force = false) {
  const uid = authStore.user?.id
  if (!uid) return
  if (!historyInflight) historyInflight = api.get('/report/history', { params: { tz: userTz() } })
    .then(({data}) => { if (authStore.user?.id === uid) history.value = data.reports || [] })
    .catch(()=>{})
    .finally(()=>{ if (authStore.user?.id === uid) historyInflight = null })
  if (cache.uid !== uid) { cache.uid = uid; cache.resp = null; cache.at = 0 }
  if (cache.resp) resp.value = cache.resp
  if (cache.example) example.value = cache.example
  else api.get('/report/week', { params: { demo: 1 } }).then(({ data }) => { cache.example = data.data; example.value = data.data }).catch(() => {})
  if (!force && cache.resp && Date.now() - cache.at < TTL) return
  // onMounted и onActivated приходят вместе при первом показе — один запрос
  if (inflight) return inflight
  inflight = api.get('/report/week', { params: { tz: userTz(), peek: 1 } })
    .then(({ data }) => {
      if (authStore.user?.id !== uid) return
      cache.resp = data
      cache.at = Date.now()
      resp.value = data
    })
    .catch(() => { /* карточка — не главное на вкладке: без связи просто не показываем */ })
    .finally(() => { if (authStore.user?.id === uid) inflight = null })
  return inflight
}

function open(demo = false) {
  if (demo) router.push({ path: '/report/week', query: { demo: '1' } })
  else router.push({ path: '/report/week', query: resp.value?.week?.start ? { start: resp.value.week.start } : {} })
  cache.at = 0
}

onMounted(() => load())
onActivated(() => load())
watch(() => authStore.user?.id, () => {
  resp.value = null
  history.value = []
  inflight = null
  historyInflight = null
  load(true)
})
</script>

<style scoped>
.wrc { margin-top: 14px; font-family: Inter, sans-serif; }
.report-banner { background: var(--ink); color: var(--cream); border-radius: 16px; padding: 18px 18px 16px; }
.report-banner h3 { font-size: var(--fs-24); font-weight: 700; line-height: 1.1; margin: 0; }
.report-example { margin: 14px -3px 18px; }
.example-placeholder { aspect-ratio: 360 / 172; }
.banner-lead, .banner-note { font-size: var(--fs-15); line-height: 1.4; }
.banner-lead { margin-top: 20px; }
.progress-count { margin: 30px 0 16px; font-size: var(--fs-24); font-weight: 700; line-height: 1.1; }
.progress-days { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: 8px; }
.progress-days span { aspect-ratio: 1; border-radius: 10px; background: var(--cream); }
.progress-days .complete { background: #A6B779; }
.banner-note { margin-top: 18px; }
.banner-open { margin-top: 24px; background: var(--cream); color: var(--ink); }
.report-observation { margin: 28px 0 30px 12px; }
.report-observation p { margin: 0; }
.report-history { margin: 22px 0; color:var(--ink); }
.report-history h3 { font-size:var(--fs-20); font-weight:700; margin-bottom:8px; }
.report-history a { display:flex; justify-content:space-between; padding:14px 0; border-bottom:1px solid var(--line); font-size:var(--fs-15); }
</style>
